import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, TextField, Chip,
    InputAdornment, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography, Box, IconButton, MenuItem,
    Tooltip
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    SettingsBackupRestore as RestoreIcon,
    Search as SearchIcon,
    CheckCircle as CheckCircleIcon,
    ErrorOutline as ErrorOutlineIcon,
    Visibility,
    VisibilityOff,
    VpnKey
} from '@mui/icons-material';
import { useAuth } from '../Auth';
import { useStaffManager } from '../hooks/useStaffManager';
import { validateEmail, validatePassword } from '../utils/validation';
import { useSnackbar } from '../context/SnackbarContext';
import { generateSecurePassword } from '../utils/validation';
import EmailContextMenu from '../components/EmailContextMenu';

const RequirementItem = ({ label, met, isFieldEmpty, tooltip = "", tooltipPlacement = "top" }) => {
    // If the field is empty, we show the neutral grey icon regardless of the "met" status
    const isActive = !isFieldEmpty && met;

    const content = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: tooltip ? 'help' : 'default' }}>
            {isActive ? (
                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
                <ErrorOutlineIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            )}
            <Typography
                variant="caption"
                sx={{
                    color: isActive ? 'success.main' : 'text.secondary',
                    lineHeight: 1
                }}
            >
                {label}
            </Typography>
        </Box>
    );

    return tooltip ? (
        <Tooltip title={tooltip} placement={tooltipPlacement} arrow>
            {content}
        </Tooltip>
    ) : content;
};

export default function UserManagement({ mode = 'staff' }) {
    const { user: currentUser, globalTenants } = useAuth();
    const tenantId = currentUser?.viewingTenantId || currentUser?.tenant_id;
    const showSnackbar = useSnackbar();
    const isCurrentUserSuperUser = currentUser?.role === 'superuser' || false;

    // Configuration based on mode
    const isSuperMode = mode === 'superuser';
    const title = isSuperMode ? "Super User Registry" : "Staff Members";
    const description = isSuperMode
        ? "System-wide administrators with global access to all tenants."
        : "Manage staff roles and access permissions for your school.";

    const apiUrl = isSuperMode
        ? `/staff`
        : `/staff?tenant_id=${tenantId}`;

    const [open, setOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [hardDeleteConfirmOpen, setHardDeleteConfirmOpen] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formUser, setFormUser] = useState({ username: '', password: '', email: '', role: isSuperMode ? 'superuser' : 'user' });
    const [searchTerm, setSearchTerm] = useState('');
    const { users, fetchUsers, handleAction, showPassword, togglePasswordVisibility } = useStaffManager(apiUrl);
    const passwordSpecs = validatePassword(formUser.password, formUser.username);
    const isPasswordValid = isEditing
        ? (formUser.password === '' || Object.entries(passwordSpecs).filter(([key]) => key !== 'isEmpty').every(([_, val]) => val))
        : Object.entries(passwordSpecs).filter(([key]) => key !== 'isEmpty').every(([_, val]) => val);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [targetUser, setTargetUser] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);


    const getRoleConfig = (role) => {
        const configs = {
            superuser: {
                // A deep, authoritative forest green
                sx: { bgcolor: '#1b5e20', color: '#00e600', fontWeight: 'bold' },
                label: 'Super User'
            },
            admin: {
                // A professional navy/indigo
                sx: { bgcolor: '#283593', color: 'white', fontWeight: 'bold' },
                label: 'Admin'
            },
            user: {
                // A soft, neutral slate blue/grey
                sx: { bgcolor: '#e8e8e8', color: 'black', border: '1px solid #cfd8dc' },
                label: 'Staff'
            }
        };
        return configs[role] || configs.user;
    };

    const filteredUsers = users.filter(u =>
        u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleOpenAdd = () => {
        setIsEditing(false);
        setFormUser({
            username: '',
            password: '',
            email: '',
            role: isSuperMode ? 'superuser' : 'user',
            tenant_id: isSuperMode ? '' : (currentUser?.viewingTenantId || currentUser?.tenant_id)
        });
        setOpen(true);
    };

    const handleEditClick = (u) => {
        setIsEditing(true);
        setFormUser({ 
            ...u, 
            email: u.email || '',
            password: '' 
        });
        setOpen(true);
    };

    const handleSave = async () => {
        // 1. Validation
        if (formUser.email && !validateEmail(formUser.email)) {
            // Assuming your handleAction or a separate snackbar can show this
            showSnackbar('Cannot save: The email address is malformed.', 'error');
            return;
        }

        // Clean relative paths
        const url = isEditing ? `/staff/${formUser.id}` : `/staff`;

        // 2. Prepare the payload
        const payload = { ...formUser };

        // 3. Handle tenant_id assignment & "Global" (null) conversion
        if (!isEditing) {
            // If Superuser is in "Super User Registry" mode, take the form value (or null if empty)
            // If a regular Admin is adding staff, force their own tenant_id
            const selectedTenant = isSuperMode ? formUser.tenant_id : (currentUser?.viewingTenantId || currentUser?.tenant_id);

            // Convert empty string (from the "System Global" MenuItem) to null
            payload.tenant_id = (selectedTenant === '' || selectedTenant === undefined) ? null : selectedTenant;
        } else {
            // Even when editing, ensure an empty string isn't sent to the DB
            payload.tenant_id = formUser.tenant_id === '' ? null : formUser.tenant_id;
        }

        const success = await handleAction(
            url,
            isEditing ? 'PUT' : 'POST',
            payload,
            `User ${isEditing ? 'updated' : 'created'}!`
        );

        if (success) setOpen(false);
        // showSnackbar (success/error) shown from handleAction
    };

    const handleConfirmDelete = async () => {
        const success = await handleAction(`/staff/${selectedUser.id}`, 'DELETE', null, 'Access revoked.');
        if (success) setDeleteConfirmOpen(false);
    };

    const handleConfirmHardDelete = async () => {
        // Only superusers should be able to hit this
        const success = await handleAction(
            `/staff/${selectedUser.id}/permanent`,
            'DELETE',
            null,
            'User permanently removed.'
        );
        if (success) {
            setHardDeleteConfirmOpen(false);
            setSelectedUser(null);
        }
    };

    const handleSendValidation = async (u) => {
        // Construct the URL for a new resend endpoint
        // We pass the user ID so the backend knows who to generate a code for
        const url = `/staff/${u.id}/resendValidationCode`;
        setSelectedUser(u);

        // Start loading
        setIsValidating(true);

        const success = await handleAction(
            url,
            'POST',
            null, // No body needed as the ID is in the URL
            `Validation email resent to ${u.email}`
        );

        // Stop loading regardless of success
        setIsValidating(false);

        if (success) {
            setVerificationCode(''); // Reset the input
            setVerifyDialogOpen(true); // Open the code entry dialog
            // Optionally refresh the list, though the status hasn't changed yet
            // fetchUsers();
        }
    };

    const handleVerifySubmit = async () => {
        const url = `/staff/validateEmailCode`;

        const success = await handleAction(
            url,
            'POST',
            { userId: selectedUser.id, code: verificationCode },
            'Email address successfully verified!'
        );

        if (success) {
            setVerifyDialogOpen(false);
            fetchUsers(); // Refresh the table to show the green checkmark
        }
    };

    // const handleContextMenu = (event, user) => {
    //     event.preventDefault();
    //     setTargetUser(user);
    //     setMenuAnchor({ mouseX: event.clientX, mouseY: event.clientY });
    // };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
        setTargetUser(null);
    };


    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>{title}</Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>{description}</Typography>

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenAdd}>
                    Add {isSuperMode ? 'Superuser' : 'Staff Member'}
                </Button>
                <TextField size="small" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ width: 300 }} />
            </Box>

            <TableContainer component={Paper} elevation={3}>
                <Table>
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell><strong>User</strong></TableCell>
                            <TableCell><strong>Email</strong></TableCell>
                            <TableCell><strong>Verified</strong></TableCell>
                            <TableCell><strong>Organisation</strong></TableCell>
                            <TableCell><strong>Role</strong></TableCell>
                            <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredUsers.map((u) => (
                            <TableRow key={u.id} hover>

                                <TableCell sx={{ opacity: u.is_active ? 1 : 0.6 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {u.username} {!u.is_active && <strong>(Archived)</strong>}
                                    </Typography>
                                </TableCell>

                                <TableCell
                                    // onContextMenu={(e) => handleContextMenu(e, u)}
                                    sx={{ cursor: 'context-menu', opacity: u.is_active ? 1 : 0.6 }}
                                >
                                    <Typography variant="body2" sx={{
                                        borderBottom: '1px dashed #ccc',
                                        display: 'inline-block'
                                    }}>
                                        {u.email}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    {u.is_email_validated ? (
                                        <Tooltip title="Email Verified">
                                            <CheckCircleIcon color="success" fontSize="small" />
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Send validation email">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ErrorOutlineIcon color="warning" fontSize="small" />
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    // Disable button and change label if this specific user is being validated
                                                    disabled={isValidating && selectedUser?.id === u.id}
                                                    onClick={() => handleSendValidation(u)}
                                                    sx={{ fontSize: '0.65rem', p: 0, minWidth: '60px' }}
                                                >
                                                    {isValidating && selectedUser?.id === u.id ? 'Sending...' : 'Validate'}
                                                </Button>
                                            </Box>
                                        </Tooltip>
                                    )}
                                </TableCell>

                                <TableCell sx={{ opacity: u.is_active ? 1 : 0.6 }}>
                                    {u.tenant_name || 'System Global'}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={getRoleConfig(u.role).label}
                                        size="small"
                                        // If the user is active, apply our custom colors. 
                                        // If deactivated, use an outlined style to show they are "off".
                                        sx={u.is_active ? getRoleConfig(u.role).sx : {
                                            color: 'text.disabled',
                                            borderColor: 'divider',
                                            fontWeight: 'normal'
                                        }}
                                        variant={u.is_active ? "filled" : "outlined"}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                        {u.is_active ? (
                                            <>
                                                <IconButton size="small" color="primary" onClick={() => handleEditClick(u)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    disabled={u.id === currentUser?.id}
                                                    onClick={() => { setSelectedUser(u); setDeleteConfirmOpen(true); }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <>
                                                <Button size="small" variant="outlined" color="success"
                                                    startIcon={<RestoreIcon />}
                                                    onClick={() => handleAction(`/staff/${u.id}/reactivate`, 'PATCH', null, 'Reactivated!')}
                                                >
                                                    Reactivate
                                                </Button>

                                                {/* HARD DELETE FOR SUPERUSERS */}
                                                {isCurrentUserSuperUser && (
                                                    <Tooltip title="Permanently Delete">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => { setSelectedUser(u); setHardDeleteConfirmOpen(true); }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* SHARED DIALOG */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>{isEditing ? 'Edit User' : 'Create User'}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Username"
                        fullWidth
                        margin="normal"
                        value={formUser.username}
                        onChange={(e) => setFormUser({ ...formUser, username: e.target.value })}
                    />

                    <TextField
                        label="Email"
                        fullWidth
                        margin="normal"
                        value={formUser.email || ''}
                        autoComplete="off"
                        // 1. Only show error if they've typed more than 10 characters
                        // 2. AND the email is actually invalid
                        error={formUser.email?.length > 10 && !validateEmail(formUser.email)}
                        helperText={
                            (formUser.email?.length > 10 && !validateEmail(formUser.email))
                                ? "Please enter a valid email address."
                                : " "
                        }
                        onChange={(e) => setFormUser({ ...formUser, email: e.target.value })}
                    />

                    {isCurrentUserSuperUser ? (
                        // Hide (and disable) the tenant selector if it's a regular Admin, not a SuperUser
                        <>
                            <TextField
                                select
                                label="Tenant"
                                fullWidth
                                margin="normal"
                                value={formUser.tenant_id || ''}
                                // Disable if it's a regular Admin (not SuperUser)
                                disabled={!isCurrentUserSuperUser}
                                onChange={(e) => setFormUser({ ...formUser, tenant_id: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{
                                    displayEmpty: true,
                                    renderValue: (selected) => {
                                        if (selected === "") {
                                            return "System Global (All Tenants)";
                                        }
                                        // Find the tenant name from globalTenants for the display
                                        const tenant = globalTenants.find(t => t.id === selected);
                                        return tenant ? tenant.name : selected;
                                    },
                                }}
                            >
                                {isSuperMode ? (
                                    [
                                        // Manually add the "System Global" option at the top
                                        <MenuItem key="global" value="">
                                            <em>System Global (All Tenants)</em>
                                        </MenuItem>,

                                        ...globalTenants.map((t) => (
                                            <MenuItem key={t.id} value={t.id}>
                                                {t.name}
                                            </MenuItem>
                                        ))
                                    ]
                                ) : (
                                    // For regular staff management, show the current locked tenant
                                    <MenuItem value={currentUser?.viewingTenantId || currentUser?.tenant_id}>
                                        {currentUser?.tenant_name || "Current Organisation"}
                                    </MenuItem>
                                )}
                            </TextField>
                        </>
                    ) : (
                        <>
                        </>
                    )}

                    < TextField select label="Role" fullWidth margin="normal" value={formUser.role} onChange={(e) => setFormUser({ ...formUser, role: e.target.value })}>
                        {isSuperMode && <MenuItem value="superuser">Super User</MenuItem>}
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="user">Staff Member</MenuItem>
                    </TextField>

                    <TextField
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        margin="normal"
                        value={formUser.password} onChange={(e) => setFormUser({ ...formUser, password: e.target.value })}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {/* Generate Password Button */}
                                    <Tooltip title="Generate secure password">
                                        <IconButton
                                            onClick={() => setFormUser({ ...formUser, password: generateSecurePassword() })}
                                            edge="end"
                                        >
                                            <VpnKey />
                                        </IconButton>
                                    </Tooltip>

                                    {/* Toggle Visibility Button */}
                                    <IconButton onClick={togglePasswordVisibility}>
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <RequirementItem label="Minimum of 8 characters" met={passwordSpecs.minLength} isFieldEmpty={passwordSpecs.isEmpty} />
                        <RequirementItem label="Contains uppercase letters" met={passwordSpecs.hasUpper} isFieldEmpty={passwordSpecs.isEmpty} tooltip="At least one capital letter (A-Z)" tooltipPlacement="right" />
                        <RequirementItem label="Contains numbers" met={passwordSpecs.hasNumber} isFieldEmpty={passwordSpecs.isEmpty} tooltip="At least one digit (0-9)" tooltipPlacement="left" />
                        <RequirementItem label="Contains special characters" met={passwordSpecs.hasSpecial} isFieldEmpty={passwordSpecs.isEmpty} tooltip="e.g. ! @ # $ % ^ & *" tooltipPlacement="right" />
                        <RequirementItem label="Not the same as username" met={passwordSpecs.notUsername} isFieldEmpty={passwordSpecs.isEmpty} tooltip="Password cannot match your username" tooltipPlacement="left" />
                        <RequirementItem label="Exclude common names" met={passwordSpecs.notCommon} isFieldEmpty={passwordSpecs.isEmpty} tooltip="Avoid obvious choices like 'password' or '123456'" tooltipPlacement="right" />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={
                            (formUser.email !== '' && !validateEmail(formUser.email)) ||
                            !isPasswordValid
                        }
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DELETE DIALOG */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to disable <strong>{selectedUser?.username}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">Confirm</Button>
                </DialogActions>
            </Dialog>


            {/* HARD DELETE DIALOG */}
            <Dialog open={hardDeleteConfirmOpen} onClose={() => setHardDeleteConfirmOpen(false)}>
                <DialogTitle sx={{ color: 'error.main' }}>Permanent Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to <strong>permanently delete</strong> "{selectedUser?.username}"?
                        This will remove them from the system entirely. This action is irreversible.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setHardDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmHardDelete} color="error" variant="contained">Delete Forever</Button>
                </DialogActions>
            </Dialog>


            {/* EMAIL VERIFICATION DIALOG */}
            <Dialog open={verifyDialogOpen} onClose={() => setVerifyDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Verify Email Address</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        A 6-digit verification code was sent to <strong>{selectedUser?.email}</strong>.
                        Please enter it below to verify this email address.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Verification Code"
                        variant="outlined"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '5px' } }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setVerifyDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleVerifySubmit}
                        disabled={verificationCode.length < 6}
                    >
                        Verify
                    </Button>
                </DialogActions>
            </Dialog>


            <EmailContextMenu
                anchor={menuAnchor}
                user={targetUser}
                onClose={handleCloseMenu}
            />
        </Box >
    );
}